/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 체크박스 엘리먼트 클래스를 정의한다.
 *
 * @file /src/elements/check/Check.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 9. 17.
 */
import * as parent from '@base/Element';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { Language, LanguageText } from '@utils/Language';
import './Check.css';

export interface Listeners<T> extends parent.Listeners<T> {
    /**
     * 포커스가 될 때
     */
    focus?: (check: T) => void;

    /**
     * 포커스를 벗어날 때
     */
    blur?: (check: T) => void;

    /**
     * 체크상태가 변경될 때
     */
    change?: (check: T, checked: boolean) => void;
}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 라벨
     */
    label?: LanguageText;

    /**
     * 라벨스타일
     */
    labelStyle?: Partial<Omit<CSSStyleDeclaration, 'width' | 'height' | 'padding' | 'margin'>>;

    /**
     * 체크타입
     */
    type?: 'check' | 'radio';

    /**
     * 표시방법
     */
    displayType?: 'input' | 'box';

    /**
     * 선택여부
     */
    checked?: boolean;

    /**
     * 체크값
     */
    value?: string;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Check extends parent.Element {
    /**
     * 엘리먼트 타입
     */
    type: string = 'check';

    /**
     * 체크타입
     */
    variant: string;

    /**
     * 라벨
     */
    label: LanguageText | null;

    /**
     * 라벨스타일
     */
    labelStyle: Partial<Omit<CSSStyleDeclaration, 'width' | 'height' | 'padding' | 'margin'>> | null;

    /**
     * 표시방법
     */
    displayType: 'input' | 'box';

    /**
     * 선택여부
     */
    checked: boolean;

    /**
     * 체크값
     */
    value: string | null;

    /**
     * 읽기전용여부
     */
    readonly: boolean = false;

    /**
     * INPUT DOM
     */
    $input: Dom<HTMLInputElement> = null;

    /**
     * 라벨 DOM
     */
    $label: Dom<HTMLSpanElement> = null;

    /**
     * 체크박스 엘리먼트 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Check> = null) {
        super(properties);

        this.variant = this.properties?.type === 'radio' ? 'radio' : 'check';
        this.label = this.properties?.label ?? null;
        this.labelStyle = this.properties?.labelStyle ?? null;
        this.displayType = this.properties?.displayType == 'box' ? 'box' : 'input';
        this.checked = this.properties?.checked === true;
        this.value = this.properties?.value ?? null;
    }

    /**
     * 엘리먼트의 DOM 을 가져온다.
     * 엘리먼트를 컴포넌트에 append 한 뒤 render() 함수를 호출하여 실제 엘리먼트의 DOM 을 처리한다.
     *
     * @returns 엘리먼트의 DOM 구조
     */
    $getElement(): Dom {
        if (this.$element == null) {
            this.$element = Html.create('label');
            this.$element.setAttr('data-element', this.type);
            this.$element.addClass(this.displayType);
        }

        return this.$element;
    }

    /**
     * INPUT DOM 을 가져온다.
     *
     * @returns INPUT DOM
     */
    $getInput(): Dom<HTMLInputElement> {
        if (this.$input == null) {
            this.$input = Html.create('input', {
                type: this.variant == 'check' ? 'checkbox' : 'radio',
            });

            this.$input.on('click', async (_me, e) => {
                if (this.readonly === true) {
                    e.preventDefault();
                    return;
                }
            });

            this.$input.on('focus', async () => {
                if (this.readonly === true) return;

                await this.fireEvent('focus', [this]);
            });

            this.$input.on('blur', async () => {
                if (this.readonly === true) return;

                await this.fireEvent('blur', [this]);
            });

            this.$input.on('change', async (me) => {
                if (this.readonly === true) return;

                this.checked = me.isChecked();
                await this.fireEvent('change', [this, me.isChecked()]);
            });
        }

        return this.$input;
    }

    /**
     * 라벨 DOM 을 가져온다.
     *
     * @returns 라벨 DOM
     */
    $getLabel(): Dom<HTMLSpanElement> {
        if (this.$label == null) {
            this.$label = Html.create('span');
            if (this.labelStyle !== null) {
                Object.assign(this.$label.getEl().style, this.labelStyle);
            }
        }

        return this.$label;
    }

    /**
     * 체크상태를 변경하거나 가져온다.
     *
     * @param checked - 체크상태
     * @param is_fire_event - 이벤트발생여부
     */
    setValue(checked: boolean, is_fire_event: boolean = true): void {
        this.$getInput().setValue(checked, is_fire_event);
    }

    /**
     * 체크가 되어 있다면 value 값을 가져온다.
     * value 가 NULL 인 경우, true 를 반환한다.
     */
    getValue(): string | boolean | null {
        return this.checked == true ? (this.value ?? true) : (this.value ?? null);
    }

    /**
     * 체크상태를 변경하거나 가져온다.
     *
     * @returns 체크상태
     */
    isChecked(): boolean {
        return this.$getInput().isChecked();
    }

    /**
     * 읽기전용상태를 변경한다.
     *
     * @param readonly - 읽기전용여부
     */
    setReadonly(readonly: boolean): void {
        this.readonly = readonly;
    }

    /**
     * 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     * @returns 컴포넌트
     */
    setDisabled(disabled: boolean): this {
        if (disabled == true) {
            this.$getInput().setAttr('disabled', 'disabled');
        } else {
            this.$getInput().removeAttr('disabled');
        }
        return this;
    }

    /**
     * 엘리먼트를 렌더링한다.
     */
    async render(): Promise<void> {
        if (this.isRendered() == true) {
            return;
        }

        if (this.isHidden() == true) {
            this.hide();
        }

        const $fragment: DocumentFragment = document.createDocumentFragment();

        $fragment.append(this.$getInput().getEl());
        $fragment.append(this.$getLabel().getEl());

        if (this.label !== null) {
            this.$getLabel().html(await Language.resolveText(this.label));
        }

        this.$getElement().append($fragment);

        if (this.checked == true) {
            this.setValue(true);
        }

        await this.onRender();
    }
}
